import { useEffect, useState } from "react";
import ClientLayout from "../components/ClientLayout";
import { CreditCard, Calendar, Hash } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import { useNavigate } from "react-router-dom";

export default function ClientPayments() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/payments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPayments(response.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate("/client/login");
        return;
      }
      toast.error("Failed to fetch payment history");
    } finally {
      setLoading(false);
    }
  };

  const getPaymentMethodBadge = (method) => {
    const colors = {
      cash: "bg-emerald-100 text-emerald-700",
      gcash: "bg-blue-100 text-blue-700",
      bank_transfer: "bg-purple-100 text-purple-700",
      others: "bg-slate-100 text-slate-700",
    };
    return (
      <span
        className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${colors[method] || colors.others}`}
      >
        {method?.replace("_", " ")}
      </span>
    );
  };

  if (loading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="p-6 lg:p-8">
        <div className="mb-8">
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">
            Payment History
          </h1>
          <p className="text-slate-600 mt-1">
            View all your payment transactions
          </p>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {payments.length === 0 ? (
            <div className="p-12 text-center">
              <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No payment records yet</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {payments.map((payment) => (
                <div
                  key={payment.id}
                  className="p-5 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <CreditCard className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">
                          ₱{payment.amount.toLocaleString()}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          {getPaymentMethodBadge(payment.payment_method)}
                          {payment.reference_number && (
                            <span className="text-xs text-slate-500 flex items-center gap-1">
                              <Hash className="w-3 h-3" />
                              {payment.reference_number}
                            </span>
                          )}
                        </div>
                        {payment.notes && (
                          <p className="text-sm text-slate-500 mt-1">
                            {payment.notes}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-500 ml-14 md:ml-0">
                      <Calendar className="w-4 h-4" />
                      {new Date(payment.created_at).toLocaleDateString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        },
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </ClientLayout>
  );
}
